import React, { useMemo } from 'react';
import {
  Box, Card, Chip, Avatar, Typography, Tooltip, LinearProgress,
  alpha, useTheme
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Edit, Delete, CheckCircle, Cancel, Block, LockReset,
  School, Security, Person, FamilyRestroom, Group, SwapHoriz, Email
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'success', icon: <CheckCircle fontSize="small" /> },
  inactive: { label: 'Inactive', color: 'default', icon: <Cancel fontSize="small" /> },
  suspended: { label: 'Suspended', color: 'error', icon: <Block fontSize="small" /> }
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

function getRoleVisual(roleName) {
  const n = (roleName || '').toLowerCase();
  if (n.includes('admin') || n.includes('principal') || n.includes('headmaster'))
    return { icon: <Security fontSize="small" />, color: '#7c3aed' };
  if (n.includes('teacher') || n.includes('classmaster') || n.includes('subjecthead'))
    return { icon: <School fontSize="small" />, color: '#2563eb' };
  if (n.includes('finance') || n.includes('bursar') || n.includes('officer'))
    return { icon: <Person fontSize="small" />, color: '#d97706' };
  if (n.includes('parent'))
    return { icon: <FamilyRestroom fontSize="small" />, color: '#059669' };
  return { icon: <Group fontSize="small" />, color: '#6b7280' };
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const UserTable = ({
  users,
  roles,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  getRoleName,
  getStatusColor,
  onEdit,
  onDelete,
  onStatusChange,
  onChangeRole,
  onResendInvite,
  onResetPassword,
  canAssignRoles = false,
  canManageUsers = false,
  canInviteUsers = false,
  canResetPasswords = false,
  canDeleteUsers = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const columns = useMemo(() => [
    {
      field: 'user',
      headerName: t('users.table.user') || 'User',
      flex: 1,
      minWidth: 240,
      renderCell: (params) => {
        const row = params.row;
        const rv = getRoleVisual(row.role || getRoleName?.(row.roleId));
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
            <Avatar
              src={row.avatarUrl || undefined}
              sx={{ bgcolor: rv.color, width: 38, height: 38, fontSize: 14, fontWeight: 700 }}
            >
              {getInitials(row.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{row.name}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{row.email}</Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'role',
      headerName: t('users.table.role') || 'Role',
      width: 170,
      renderCell: (params) => {
        const roleName = params.row.role || getRoleName?.(params.row.roleId) || '';
        const rv = getRoleVisual(roleName);
        return (
          <Chip
            icon={rv.icon}
            label={roleName}
            size="small"
            sx={{
              bgcolor: alpha(rv.color, 0.1),
              color: rv.color,
              fontWeight: 600,
              '& .MuiChip-icon': { color: rv.color }
            }}
          />
        );
      }
    },
    {
      field: 'status',
      headerName: t('users.table.status') || 'Status',
      width: 130,
      renderCell: (params) => {
        const sc = STATUS_CONFIG[params.row.status] || STATUS_CONFIG.inactive;
        return (
          <Chip
            icon={sc.icon}
            label={sc.label}
            color={sc.color}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        );
      }
    },
    {
      field: 'phone',
      headerName: t('users.table.phone') || 'Phone',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" color={params.row.phone ? 'text.primary' : 'text.disabled'}>
          {params.row.phone || '—'}
        </Typography>
      )
    },
    {
      field: 'lastActive',
      headerName: t('users.table.lastActive') || 'Last Active',
      width: 140,
      renderCell: (params) => {
        const val = params.row.lastActive;
        const isRecent = val && (Date.now() - new Date(val).getTime()) < 24 * 60 * 60 * 1000;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isRecent && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />}
            <Typography variant="body2" color={val ? 'text.primary' : 'text.disabled'}>
              {timeAgo(val)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.createdAt ? new Date(params.row.createdAt).toLocaleDateString() : '—'}
        </Typography>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('users.table.actions') || 'Actions',
      width: 160,
      getActions: (params) => {
        const row = params.row;
        const roleLower = (row.role || getRoleName?.(row.roleId) || '').toLowerCase();
        const isParent = roleLower === 'parent';
        const actions = [
          <GridActionsCellItem key="edit" icon={<Tooltip title={t('common.edit') || 'Edit'}><Edit /></Tooltip>} label="Edit" onClick={() => onEdit(row.id)} />,
        ];
        if (canAssignRoles) {
          actions.push(
            <GridActionsCellItem key="role" icon={<SwapHoriz />} label={t('users.changeRole') || 'Change Role'} onClick={() => onChangeRole(row)} showInMenu />
          );
        }
        if (canResetPasswords) {
          actions.push(
            <GridActionsCellItem key="reset" icon={<LockReset />} label={t('users.table.resetPassword') || 'Reset Password'} onClick={() => onResetPassword(row.id)} showInMenu />
          );
        }
        if (canInviteUsers) {
          actions.push(
            <GridActionsCellItem key="invite" icon={<Email />} label={t('users.table.resendInvite') || 'Resend Invite'} onClick={() => onResendInvite(row.id)} showInMenu />
          );
        }
        if (canManageUsers) {
          if (row.status === 'active') {
            actions.push(
              <GridActionsCellItem key="suspend" icon={<Block />} label={t('users.table.suspend') || 'Suspend'} onClick={() => onStatusChange(row.id, 'suspended')} showInMenu />
            );
            actions.push(
              <GridActionsCellItem key="deactivate" icon={<Cancel />} label={t('users.table.deactivate') || 'Deactivate'} onClick={() => onStatusChange(row.id, 'inactive')} showInMenu />
            );
          } else {
            actions.push(
              <GridActionsCellItem key="activate" icon={<CheckCircle />} label={t('users.table.activate') || 'Activate'} onClick={() => onStatusChange(row.id, 'active')} showInMenu />
            );
          }
        }
        if (canDeleteUsers && !isParent) {
          actions.push(
            <GridActionsCellItem key="delete" icon={<Delete color="error" />} label={t('common.delete') || 'Delete'} onClick={() => onDelete(row.id)} showInMenu />
          );
        }
        return actions;
      }
    }
  ], [t, getRoleName, canAssignRoles, canManageUsers, canInviteUsers, canResetPasswords, canDeleteUsers, onEdit, onDelete, onStatusChange, onChangeRole, onResendInvite, onResetPassword]);

  return (
    <Card>
      <Box sx={{ height: 560, width: '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
          rowCount={total}
          pagination
          paginationMode="client"
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => onPageChange(p)}
          onPageSizeChange={(s) => onPageSizeChange(s)}
          rowsPerPageOptions={[5, 10, 25, 50]}
          rowHeight={60}
          disableSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              fontWeight: 600,
            },
            '& .MuiDataGrid-row:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }
          }}
        />
      </Box>
    </Card>
  );
};

export default UserTable;